/**
 * Reusable Content Blocks for SEO Pages
 * =====================================
 * Pre-built, factual content components that can be assembled into SEO pages.
 * All content is sourced from ContentRegistry - never fabricated.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, TrendingDown, Zap, Shield, Smartphone, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  ENKEL_PRODUCTS, 
  SERVICE_AGREEMENT, 
  LADERABAT, 
  SMART_CHARGE, 
  EV_CONSTANTS,
  TRUST_BADGES,
  INTERNAL_LINKS 
} from './ContentRegistry';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
const sans = { fontFamily: '"DM Sans", "Space Grotesk", sans-serif' };

// ============================================
// PRICING COMPARISON BAR
// ============================================
export function PriceComparisonBar({ label, value, maxValue, isEnkel = false, showSavings = false, savingsAmount = 0 }) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className={`font-semibold ${isEnkel ? 'text-[#1a2e1a]' : 'text-gray-700'}`}>{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-bold">{value.toLocaleString('da-DK')} kr./år</span>
          {showSavings && savingsAmount > 0 && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
              −{savingsAmount.toLocaleString('da-DK')} kr.
            </span>
          )}
        </div>
      </div>
      <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
        <div
          className={`h-full rounded-lg transition-all ${isEnkel ? 'bg-[#eab308]' : 'bg-gray-400'}`}
          style={{ width: `${Math.max(pct, 8)}%` }}
        />
      </div>
    </div>
  );
}

// ============================================
// SERVICE AGREEMENT SUMMARY BLOCK
// ============================================
export function ServiceAgreementBlock({ variant = 'full' }) {
  if (variant === 'compact') {
    return (
      <div className="bg-[#e8f5e9] border border-[#0f5c4a]/20 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-5 h-5 text-[#0f5c4a]" />
          <h4 className="font-bold text-gray-900">Serviceaftale inkluderet</h4>
          <span className="ml-auto text-[#0f5c4a] font-bold">0 kr./md.</span>
        </div>
        <ul className="grid grid-cols-2 gap-2 text-sm">
          {SERVICE_AGREEMENT.includes.slice(0, 4).map((item, i) => (
            <li key={i} className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#0f5c4a] flex-shrink-0" />
              <span className="text-gray-700">{item.label}</span>
            </li>
          ))}
        </ul>
        <Link to="/el-fordele#serviceaftale" className="text-[#0f5c4a] text-sm font-semibold hover:underline mt-3 inline-block">
          Læs mere om serviceaftale →
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#e8f5e9] border border-[#0f5c4a]/20 rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#0f5c4a] rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#eab308]" />
          </div>
          <div>
            <h3 className="font-bold text-xl text-gray-900" style={serif}>Serviceaftale</h3>
            <p className="text-sm text-gray-600">Alt inkluderet til én pris</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-3xl font-bold text-[#0f5c4a]">0 kr.</span>
          <span className="text-gray-500 text-sm">/md.</span>
          <p className="text-xs text-gray-500">Værdi: {SERVICE_AGREEMENT.marketPrice} kr./md. hos andre</p>
        </div>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICE_AGREEMENT.includes.map((item, i) => (
          <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
            <div className="w-8 h-8 bg-[#0f5c4a] rounded-lg flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-[#eab308]" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">{item.label}</div>
              <div className="text-xs text-gray-500">{item.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// LADERABAT (CHARGING REBATE) BLOCK
// ============================================
export function LaderabatBlock({ kmPerYear = 40000, homeChargePct = 0.90 }) {
  const evKwh = EV_CONSTANTS.calculateEvKwh(kmPerYear, homeChargePct);
  const rebateYearly = EV_CONSTANTS.calculateLaderabat(evKwh);
  const subscriptionYearly = ENKEL_PRODUCTS.subscriptions.kvartal.yearlyPrice;
  const netCost = Math.max(0, subscriptionYearly - rebateYearly);

  return (
    <div className="bg-gradient-to-br from-[#2d4a47] to-[#1a2e2b] rounded-2xl p-6 md:p-8 text-white">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-6 h-6 text-[#eab308]" />
        <h3 className="font-bold text-xl" style={serif}>Laderabat</h3>
        <span className="ml-auto bg-[#eab308] text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
          10 øre/kWh
        </span>
      </div>
      
      <p className="text-gray-300 mb-6">
        Automatisk rabat på al hjemmeopladning via enkel Saving Mode app. 
        Fratrækkes direkte fra din el-regning.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <p className="text-sm text-white/70 mb-1">Hjemmeopladning</p>
          <p className="text-2xl font-bold text-white">{evKwh.toLocaleString('da-DK')} kWh</p>
          <p className="text-xs text-white/50">ved {kmPerYear.toLocaleString('da-DK')} km/år</p>
        </div>
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <p className="text-sm text-white/70 mb-1">Din laderabat</p>
          <p className="text-2xl font-bold text-[#eab308]">{rebateYearly.toLocaleString('da-DK')} kr.</p>
          <p className="text-xs text-white/50">per år</p>
        </div>
        <div className="bg-[#eab308] rounded-xl p-4 text-gray-900">
          <p className="text-sm text-gray-700 mb-1">Effektivt abonnement</p>
          <p className="text-2xl font-bold">{netCost.toLocaleString('da-DK')} kr.</p>
          <p className="text-xs text-gray-600">per år efter rabat</p>
        </div>
      </div>

      <Link to="/el-fordele#laderabat" className="text-[#eab308] font-semibold hover:underline text-sm">
        Læs mere om laderabat →
      </Link>
    </div>
  );
}

// ============================================
// SAVING MODE BENEFITS BLOCK
// ============================================
export function SmartChargeBlock({ variant = 'light' }) {
  const bgClass = variant === 'dark' ? 'bg-[#1a3a32]' : 'bg-white';
  const textClass = variant === 'dark' ? 'text-white' : 'text-gray-900';
  const subtextClass = variant === 'dark' ? 'text-gray-300' : 'text-gray-600';

  return (
    <div className={`${bgClass} rounded-2xl p-6 md:p-8 border ${variant === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#eab308] rounded-xl flex items-center justify-center">
          <TrendingDown className="w-6 h-6 text-gray-900" />
        </div>
        <div>
          <h3 className={`font-bold text-xl ${textClass}`} style={serif}>Saving Mode</h3>
          <p className={`text-sm ${subtextClass}`}>Automatisk opladning i billige timer</p>
        </div>
        <div className="ml-auto text-right">
          <span className={`text-2xl font-bold ${variant === 'dark' ? 'text-[#eab308]' : 'text-[#1a2e1a]'}`}>
            Op til ~{SMART_CHARGE.estimatedSavingsPct}%*
          </span>
          <p className={`text-xs ${subtextClass}`}>potentiel besparelse</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        {SMART_CHARGE.features.map((feature, i) => (
          <div key={i} className={`rounded-xl p-4 ${variant === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'}`}>
            <h4 className={`font-semibold text-sm mb-1 ${textClass}`}>{feature.label}</h4>
            <p className={`text-xs ${subtextClass}`}>{feature.description}</p>
          </div>
        ))}
      </div>

      <p className={`text-xs ${subtextClass} italic`}>
        {SMART_CHARGE.disclaimer}
      </p>
    </div>
  );
}

// ============================================
// CHARGER PRODUCT CARD
// ============================================
export function ChargerProductCard({ chargerId, showPrice = true, compact = false }) {
  const charger = ENKEL_PRODUCTS.chargers[chargerId];
  if (!charger) return null;

  if (compact) {
    return (
      <Link to={`/${charger.slug}`} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-200 hover:border-[#1a2e1a] transition-all">
        <img src={charger.image} alt={charger.name} className="w-16 h-16 object-contain" />
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 text-sm">{charger.name}</h4>
          {showPrice && (
            <p className="text-[#1a2e1a] font-bold">{charger.price.toLocaleString('da-DK')} kr.</p>
          )}
        </div>
        <Check className="w-5 h-5 text-[#1a2e1a]" />
      </Link>
    );
  }

  return (
    <Link to={`/${charger.slug}`} className="group">
      <div className="bg-white rounded-xl p-6 border-2 border-transparent hover:border-[#1a2e1a] transition-all shadow-sm">
        <div className="w-32 h-32 mx-auto mb-4">
          <img src={charger.image} alt={charger.name} className="w-full h-full object-contain" />
        </div>
        <h3 className="font-bold text-center mb-1 text-gray-900">{charger.name}</h3>
        {showPrice && (
          <p className="text-sm text-gray-500 text-center">
            {charger.originalPrice > charger.price && (
              <span className="line-through text-gray-400 mr-1">{charger.originalPrice.toLocaleString('da-DK')} kr.</span>
            )}
            <span className={charger.originalPrice > charger.price ? 'text-[#1a2e1a] font-semibold' : ''}>
              {charger.price.toLocaleString('da-DK')} kr.
            </span>
          </p>
        )}
        <div className="flex items-center justify-center gap-1 mt-2">
          <Check className="w-4 h-4 text-[#1a2e1a]" />
          <span className="text-xs text-[#1a2e1a] font-medium">Saving Mode inkluderet</span>
        </div>
      </div>
    </Link>
  );
}

// ============================================
// TRUST BADGES ROW
// ============================================
export function TrustBadgesRow({ variant = 'light' }) {
  const textClass = variant === 'dark' ? 'text-white' : 'text-gray-900';
  
  return (
    <div className="flex flex-wrap items-center justify-center gap-6">
      <a 
        href={TRUST_BADGES.stromligning.link} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
      >
        <span className={`text-sm font-bold ${textClass}`}>{TRUST_BADGES.stromligning.score}</span>
        <img src={TRUST_BADGES.stromligning.logo} alt={TRUST_BADGES.stromligning.name} className="h-6 w-auto" />
      </a>
      <a 
        href={TRUST_BADGES.elbiil.link} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
      >
        <span className={`text-sm font-bold ${textClass}`}>{TRUST_BADGES.elbiil.score}</span>
        <img src={TRUST_BADGES.elbiil.logo} alt={TRUST_BADGES.elbiil.name} className="h-6 w-auto" />
      </a>
    </div>
  );
}

// ============================================
// EV SAVINGS CALCULATOR (SIMPLIFIED)
// ============================================
export function EVSavingsCalculator({ elSavings = 0, competitorName = '' }) {
  const [kmPerYear, setKmPerYear] = React.useState(40000);
  const [homeChargePct] = React.useState(0.90);

  const evKwh = EV_CONSTANTS.calculateEvKwh(kmPerYear, homeChargePct);
  const laderabat = EV_CONSTANTS.calculateLaderabat(evKwh);
  const smartCharge = EV_CONSTANTS.calculateSmartChargeSavings(evKwh);
  const serviceAgreement = SERVICE_AGREEMENT.yearlyValue;
  const totalEvSavings = laderabat + smartCharge + serviceAgreement;
  const grandTotal = elSavings + totalEvSavings;

  return (
    <div className="bg-[#f5f3ee] rounded-2xl p-6 md:p-8 border border-[#e8e4d9]">
      <h3 className="text-xl font-bold text-gray-900 mb-4 text-center" style={serif}>
        Beregn din samlede besparelse med elbil
      </h3>
      
      <div className="mb-6">
        <label className="text-sm font-semibold text-gray-700 block mb-2">Årlig kørsel</label>
        <input
          type="range"
          min="5000"
          max="40000"
          step="1000"
          value={kmPerYear}
          onChange={(e) => setKmPerYear(parseInt(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>5.000 km</span>
          <span className="font-bold text-[#1a2e1a]">{kmPerYear.toLocaleString('da-DK')} km/år</span>
          <span>40.000 km</span>
        </div>
      </div>

      <div className="space-y-3 mb-6">
        {elSavings > 0 && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <div>
              <span className="text-gray-700">Besparelse vs. {competitorName || 'nuværende'}</span>
              <span className="text-xs text-gray-400 block">el-aftale (abonnement + tillæg)</span>
            </div>
            <span className="font-bold text-gray-900">{elSavings.toLocaleString('da-DK')} kr./år</span>
          </div>
        )}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="text-gray-700">Laderabat (10 øre/kWh)</span>
          <span className="font-bold text-gray-900">+{laderabat.toLocaleString('da-DK')} kr./år</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="text-gray-700">Saving Mode (~22% besparelse)</span>
          <span className="font-bold text-gray-900">+{smartCharge.toLocaleString('da-DK')} kr./år</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="text-gray-700">Serviceaftale (værdi)</span>
          <span className="font-bold text-gray-900">+{serviceAgreement.toLocaleString('da-DK')} kr./år</span>
        </div>
      </div>

      <div className="bg-[#1a2e1a] rounded-xl p-5 text-white">
        <div className="flex justify-between items-center">
          <span className="font-semibold">Samlet potentiel besparelse</span>
          <span className="text-3xl font-bold text-[#eab308]">~{grandTotal.toLocaleString('da-DK')} kr./år</span>
        </div>
        <p className="text-xs text-white/60 mt-2">
          Ved {kmPerYear.toLocaleString('da-DK')} km/år med 90% hjemmeopladning
        </p>
      </div>
    </div>
  );
}

// ============================================
// FEATURES GRID (ENKEL BENEFITS)
// ============================================
export function EnkelFeaturesGrid() {
  const features = [
    { icon: <Zap className="w-5 h-5" />, label: 'Saving Mode', value: '0 kr./md.' },
    { icon: <Shield className="w-5 h-5" />, label: 'Serviceaftale', value: '0 kr./md.' },
    { icon: <TrendingDown className="w-5 h-5" />, label: 'Laderabat', value: '10 øre/kWh' },
    { icon: <Clock className="w-5 h-5" />, label: 'Ingen binding', value: 'Fleksibelt' },
  ];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {features.map((f, i) => (
        <div key={i} className="bg-white/10 rounded-xl p-4 text-center border border-white/10">
          <div className="w-10 h-10 bg-[#eab308] rounded-lg flex items-center justify-center mx-auto mb-2 text-gray-900">
            {f.icon}
          </div>
          <p className="text-white font-semibold text-sm">{f.label}</p>
          <p className="text-[#eab308] font-bold">{f.value}</p>
        </div>
      ))}
    </div>
  );
}

// ============================================
// CTA SECTION
// ============================================
export function CTASection({ 
  headline = 'Klar til at spare på din elregning?', 
  subtext = 'Skift til enkel energi – det tager kun 5 minutter',
  primaryCTA = { path: '/elaftale-b', text: 'Se el-aftaler' },
  secondaryCTA = { path: '/opladere', text: 'Se ladebokse' },
  variant = 'dark'
}) {
  const bgClass = variant === 'dark' ? 'bg-[#1a3a32]' : 'bg-[#f5f3ee]';
  const headlineClass = variant === 'dark' ? 'text-white' : 'text-[#1a2e1a]';
  const subtextClass = variant === 'dark' ? 'text-gray-300' : 'text-gray-600';

  return (
    <section className={`py-16 ${bgClass}`}>
      <div className="container mx-auto px-6 text-center">
        <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${headlineClass}`} style={serif}>
          {headline}
        </h2>
        <p className={`${subtextClass} mb-6 max-w-lg mx-auto`}>{subtext}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={primaryCTA.path}>
            <Button size="lg" className="bg-[#eab308] hover:bg-[#ca9a06] text-gray-900 font-bold">
              {primaryCTA.text} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to={secondaryCTA.path}>
            <Button 
              size="lg" 
              className={`border-2 font-semibold ${variant === 'dark' ? 'bg-white text-gray-900 border-white hover:bg-gray-100' : 'bg-[#1a2e1a] text-white border-[#1a2e1a] hover:bg-[#0f3f2f]'}`}
            >
              {secondaryCTA.text}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================
// FAQ ACCORDION
// ============================================
export function FAQAccordion({ faqs, maxItems = 6 }) {
  const displayFaqs = faqs?.slice(0, maxItems) || [];
  
  if (displayFaqs.length === 0) return null;

  return (
    <div className="space-y-3">
      {displayFaqs.map((faq, i) => (
        <details key={i} className="bg-gray-50 rounded-xl border border-gray-200 group">
          <summary className="px-6 py-4 cursor-pointer font-semibold flex items-center justify-between text-gray-900">
            {faq.question}
            <span className="group-open:rotate-45 transition-transform text-2xl ml-4 flex-shrink-0">+</span>
          </summary>
          <div className="px-6 pb-4 text-gray-600 leading-relaxed">{faq.answer}</div>
        </details>
      ))}
    </div>
  );
}

export default {
  PriceComparisonBar,
  ServiceAgreementBlock,
  LaderabatBlock,
  SmartChargeBlock,
  ChargerProductCard,
  TrustBadgesRow,
  EVSavingsCalculator,
  EnkelFeaturesGrid,
  CTASection,
  FAQAccordion
};